import React, { useEffect, useMemo, useState } from "react";
import { AlertTriangle, ArrowRight, CheckCheck } from "lucide-react";

/**
 * Session 30.18 / 31.2 — Long-wait alerts.
 *
 * Scans every open table session for order LINES still waiting (placed but
 * not yet marked ready/delivered by the kitchen or bar) for longer than the
 * threshold that applies to that line's DEPARTMENT — e.g. drinks 10 min,
 * food 30 min (owner-configurable under Menu Settings → Service alerts).
 * Lines already ready never count.
 *
 * Session 31.2 — one compact row per late order (no giant pulsing block,
 * no duplicate toasts): "Table 5 · Order 12 · 18 min · 2× Burger, 1× Coke".
 * Re-renders every 15 s so elapsed times keep moving.
 *
 * `onSelect(sessionId)` jumps to that table. Shared by the Staff dashboard
 * and the Owner/Manager Overview.
 */
export const LONG_WAIT_MIN = 15;
// Session 31.9 — nobody waits half a day for a plate. Orders older than this
// are forgotten tickets, not live waits; they stay on the table view but
// no longer pin the alert list (a client had 8 000-minute alerts that never
// cleared).
export const LONG_WAIT_MAX_MIN = 12 * 60;

const DONE_STATUSES = new Set(["ready", "delivered", "served", "completed", "voided"]);

/** A line is "being prepared" only when a station will actually tick it.
 *  The server tags each item with `station: "kitchen" | "bar" | null`
 *  (Session 31.9); lines with no station (e.g. a bottle the waiter hands
 *  over) can never be marked ready, so they must not alert. Payloads built
 *  offline may lack the field — then every line counts, as before. */
const isPreparedSomewhere = (it) => !(it && Object.prototype.hasOwnProperty.call(it, "station") && it.station === null);

/** Session 72 — SAME RULE AS THE STATIONS. The server stamps every line
 *  with `awaiting_station` using exactly the gates the Kitchen / Bar list
 *  queries apply (live order, not held for confirmation, not pay-before-
 *  kitchen, station not done). When the flag is present it is the truth:
 *  a line the kitchen/bar do not list can never be "waiting". Offline /
 *  optimistic payloads without the flag fall back to the local heuristic. */
export const isLineWaiting = (it) => {
  if (!it || it.voided) return false;
  // A done status always wins (a WS "ready" delta may land before the
  // snapshot that refreshes the server flag).
  if (DONE_STATUSES.has(String(it.status || "").toLowerCase())) return false;
  if (Object.prototype.hasOwnProperty.call(it, "awaiting_station")) return it.awaiting_station === true;
  return isPreparedSomewhere(it);
};

export function thresholdForDepartment(department, thresholdMin = LONG_WAIT_MIN, deptThresholds) {
  const key = String(department || "").trim().toLowerCase();
  const override = deptThresholds && key ? Number(deptThresholds[key]) : NaN;
  return Number.isFinite(override) && override > 0 ? override : Number(thresholdMin) || LONG_WAIT_MIN;
}

export function longWaitOrders(sessions, thresholdMin = LONG_WAIT_MIN, now = Date.now(), deptThresholds = null) {
  const out = [];
  (sessions || []).forEach((s) => {
    if (!s || s.status !== "open") return;
    (s.orders || []).forEach((o) => {
      if (!o || !["pending", "confirmed"].includes(String(o.status || "").toLowerCase())) return;
      if (o.awaiting_staff_confirmation) return; // held for approval — not cooking yet
      if (String(o.payment_status || "") === "awaiting_payment") return; // pay-before-kitchen: stations don't see it yet
      const started = new Date(o.created_at || o.placed_at || o.updated_at || now).getTime();
      const mins = (now - started) / 60000;
      if (mins > LONG_WAIT_MAX_MIN) return;
      // Only lines still being prepared, judged against their own department limit.
      const waiting = (o.items || []).filter((it) => isLineWaiting(it));
      const late = waiting.filter((it) => mins >= thresholdForDepartment(it.department, thresholdMin, deptThresholds));
      if (late.length === 0) return;
      const threshold = Math.min(...late.map((it) => thresholdForDepartment(it.department, thresholdMin, deptThresholds)));
      out.push({
        sessionId: s.id,
        tableNumber: s.table_number,
        assigned: s.assigned_user_name || "",
        orderId: o.id,
        orderNumber: o.order_number,
        status: String(o.status || "").toLowerCase(),
        minutes: Math.floor(mins),
        threshold,
        items: late,
        count: late.reduce((n, it) => n + Number(it.quantity || 0), 0),
        departments: Array.from(new Set(late.map((it) => String(it.department || "").trim()).filter(Boolean))),
      });
    });
  });
  out.sort((a, b) => (b.minutes - b.threshold) - (a.minutes - a.threshold));
  return out;
}

/** Session ids that currently have a long-wait order (for chip highlighting). */
export function longWaitSessionIds(sessions, thresholdMin = LONG_WAIT_MIN, now = Date.now(), deptThresholds = null) {
  return new Set(longWaitOrders(sessions, thresholdMin, now, deptThresholds).map((x) => x.sessionId));
}

const tableLabel = (n) => (/^\d+$/.test(String(n)) ? `Table ${n}` : String(n || ""));

/**
 * `onMarkServed(orderId)` (optional, Session 72) renders a "Served" action on
 * every row: everything on that order has gone out, regardless of what the
 * station screens did — clears the alert on EVERY device via the server.
 */
export function LongWaitAlerts({ sessions, onSelect, onMarkServed = null, busyOrderId = null, thresholdMin = LONG_WAIT_MIN, deptThresholds = null, testIdPrefix = "long-wait" }) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 15000);
    return () => clearInterval(t);
  }, []);
  const alerts = useMemo(() => longWaitOrders(sessions, thresholdMin, now, deptThresholds), [sessions, thresholdMin, now, deptThresholds]);
  const [showAll, setShowAll] = useState(false);
  const LIMIT = 5;
  if (alerts.length === 0) return null;
  const visible = showAll ? alerts : alerts.slice(0, LIMIT);
  const hasOverrides = deptThresholds && Object.keys(deptThresholds).length > 0;

  return (
    <section
      role="alert"
      aria-live="polite"
      className="w-full min-w-0 max-w-full overflow-hidden rounded-2xl border border-rose-300/70 bg-card dark:border-rose-800/60"
      data-testid={`${testIdPrefix}-alerts`}
    >
      <div className="flex items-center justify-between gap-2 border-b border-rose-200/70 px-3 py-2 dark:border-rose-900/50">
        <p className="flex min-w-0 items-center gap-1.5 text-xs font-semibold text-rose-800 dark:text-rose-200">
          <AlertTriangle className="h-4 w-4 shrink-0" />
          <span className="truncate">
            {alerts.length === 1 ? "1 order" : `${alerts.length} orders`} waiting too long
            {hasOverrides ? "" : ` (over ${thresholdMin} min)`}
          </span>
        </p>
        <span className="shrink-0 rounded-full bg-rose-600 px-2 py-0.5 text-[11px] font-bold text-white" data-testid={`${testIdPrefix}-count`}>
          {alerts.length}
        </span>
      </div>
      <ul className="divide-y divide-border" data-testid={`${testIdPrefix}-list`}>
        {visible.map((a) => (
          <li key={`${a.sessionId}-${a.orderId}`} className="flex flex-wrap items-center">
            <button
              type="button"
              onClick={() => onSelect?.(a.sessionId)}
              className="group flex min-w-0 flex-1 basis-[17rem] items-center gap-3 px-3 py-2 text-left transition-colors hover:bg-rose-50/70 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-rose-500 dark:hover:bg-rose-950/30"
              data-testid={`${testIdPrefix}-row-${a.orderId || a.sessionId}`}
            >
              <span className="shrink-0 rounded-lg bg-rose-600 px-2 py-1 text-xs font-bold tabular-nums text-white" data-testid={`${testIdPrefix}-minutes-${a.orderId || a.sessionId}`}>
                {a.minutes} min
              </span>
              <span className="min-w-0 flex-1">
                <span className="block truncate text-sm font-semibold">
                  {tableLabel(a.tableNumber)}
                  {a.orderNumber ? <span className="ml-1.5 font-medium text-muted-foreground">· {a.orderNumber}</span> : null}
                  {a.assigned ? <span className="ml-1.5 text-xs font-normal text-muted-foreground">· {a.assigned}</span> : null}
                </span>
                <span className="block truncate text-xs text-muted-foreground" data-testid={`${testIdPrefix}-items-${a.orderId || a.sessionId}`}>
                  {a.items.map((it) => `${it.quantity}× ${it.name}`).join(", ")}
                  {hasOverrides && a.departments.length ? <span className="ml-1 uppercase tracking-wide opacity-70">· {a.departments.join(" / ")} limit {a.threshold} min</span> : null}
                </span>
              </span>
              <ArrowRight className="h-4 w-4 shrink-0 text-muted-foreground transition-transform group-hover:translate-x-0.5" />
            </button>
            {onMarkServed && a.orderId && !String(a.orderId).startsWith("local-") ? (
              <div className="ml-auto flex shrink-0 justify-end px-3 py-1.5">
                <button
                  type="button"
                  disabled={busyOrderId === a.orderId}
                  onClick={(e) => { e.stopPropagation(); onMarkServed(a.orderId); }}
                  className="inline-flex h-8 items-center gap-1 rounded-lg border border-border px-2.5 text-xs font-semibold text-foreground transition-colors hover:bg-secondary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-rose-500 disabled:opacity-50"
                  title="Everything on this order has gone out — clear it everywhere"
                  data-testid={`${testIdPrefix}-served-${a.orderId}`}
                >
                  <CheckCheck className="h-3.5 w-3.5" />
                  {busyOrderId === a.orderId ? "Saving…" : "Mark served"}
                </button>
              </div>
            ) : null}
          </li>
        ))}
      </ul>
      {alerts.length > LIMIT ? (
        <button
          type="button"
          onClick={() => setShowAll((v) => !v)}
          className="w-full border-t border-border py-1.5 text-xs font-semibold text-rose-800 hover:bg-rose-50/70 dark:text-rose-200 dark:hover:bg-rose-950/30"
          data-testid={`${testIdPrefix}-toggle-all`}
        >
          {showAll ? `Show the ${LIMIT} longest only` : `Show all ${alerts.length}`}
        </button>
      ) : null}
    </section>
  );
}
